import { supabase } from '../auth/supabase-client';
import { slugifier } from './slug';
import type {
  Member,
  MemberContact,
  MemberPole,
  MemberRole,
  Pole,
  PoleFamily,
  Site,
  Workspace,
} from './types';

function client() {
  if (!supabase) throw new Error('Supabase indisponible.');
  return supabase;
}

export type Annuaire = {
  sites: Site[];
  poles: Pole[];
  members: Member[];
  memberPoles: MemberPole[];
  contacts: MemberContact[];
};

export async function chargerEspaces(): Promise<Workspace[]> {
  const { data, error } = await client()
    .from('workspaces')
    .select('*')
    .eq('active', true)
    .order('kind')
    .order('name');
  if (error) throw error;
  return data ?? [];
}

/** Ligne d'annuaire de la personne connectée dans l'espace demandé. */
export async function chargerMonMembre(workspaceId: string): Promise<Member | null> {
  const { data: session } = await client().auth.getUser();
  const userId = session.user?.id;
  if (!userId) return null;

  const { data, error } = await client()
    .from('members')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function chargerAnnuaire(workspaceId: string): Promise<Annuaire> {
  const [sites, poles, members] = await Promise.all([
    client().from('sites').select('*').eq('workspace_id', workspaceId).order('position'),
    client().from('poles').select('*').eq('workspace_id', workspaceId).order('position'),
    client().from('members').select('*').eq('workspace_id', workspaceId).order('display_name'),
  ]);

  for (const reponse of [sites, poles, members]) {
    if (reponse.error) throw reponse.error;
  }

  const memberIds = (members.data ?? []).map((membre) => membre.id);
  const [memberPoles, contacts] = await Promise.all([
    memberIds.length > 0
      ? client().from('member_poles').select('*').in('member_id', memberIds)
      : Promise.resolve({ data: [], error: null }),
    memberIds.length > 0
      ? client().from('member_contacts').select('*').in('member_id', memberIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (memberPoles.error) throw memberPoles.error;
  if (contacts.error) throw contacts.error;

  return {
    sites: sites.data ?? [],
    poles: poles.data ?? [],
    members: members.data ?? [],
    memberPoles: memberPoles.data ?? [],
    contacts: contacts.data ?? [],
  };
}

/** Le nom que la personne veut voir d'elle-même. Elle seule peut le changer. */
export async function changerMonNom(workspaceId: string, nom: string): Promise<Member> {
  const { data, error } = await client().rpc('changer_mon_nom', {
    p_workspace: workspaceId,
    p_nom: nom,
  });
  if (error) throw error;
  return data;
}

export async function enregistrerMembre(
  memberId: string,
  valeurs: { role: MemberRole; site_id: string | null; fonction: string | null; active: boolean },
): Promise<void> {
  const { error } = await client().from('members').update(valeurs).eq('id', memberId);
  if (error) throw error;
}

/** Remplace les rattachements d'une personne par la sélection donnée. */
export async function definirPolesDuMembre(
  memberId: string,
  rattachements: { pole_id: string; is_manager: boolean }[],
): Promise<void> {
  const conserves = rattachements.map((rattachement) => rattachement.pole_id);
  const suppression = client().from('member_poles').delete().eq('member_id', memberId);
  const { error: erreurSuppression } = await (conserves.length > 0
    ? suppression.not('pole_id', 'in', `(${conserves.join(',')})`)
    : suppression);
  if (erreurSuppression) throw erreurSuppression;

  if (rattachements.length === 0) return;
  const { error } = await client()
    .from('member_poles')
    .upsert(
      rattachements.map((rattachement) => ({ member_id: memberId, ...rattachement })),
      { onConflict: 'member_id,pole_id' },
    );
  if (error) throw error;
}

export async function definirResponsableDePole(
  poleId: string,
  memberId: string | null,
): Promise<void> {
  const { error } = await client()
    .from('poles')
    .update({ manager_member_id: memberId })
    .eq('id', poleId);
  if (error) throw error;

  if (!memberId) return;
  const { error: erreurRattachement } = await client()
    .from('member_poles')
    .upsert(
      { member_id: memberId, pole_id: poleId, is_manager: true },
      { onConflict: 'member_id,pole_id' },
    );
  if (erreurRattachement) throw erreurRattachement;
}

export async function mettreAJourSite(
  siteId: string,
  valeurs: { name?: string; active?: boolean },
): Promise<void> {
  const { error } = await client().from('sites').update(valeurs).eq('id', siteId);
  if (error) throw error;
}

export async function creerSite(
  workspaceId: string,
  name: string,
  position: number,
): Promise<void> {
  const { error } = await client()
    .from('sites')
    .insert({ workspace_id: workspaceId, name, slug: slugifier(name), position });
  if (error) throw error;
}

export async function mettreAJourPole(
  poleId: string,
  valeurs: { name?: string; active?: boolean; site_id?: string | null },
): Promise<void> {
  const { error } = await client().from('poles').update(valeurs).eq('id', poleId);
  if (error) throw error;
}

export async function creerPole(
  workspaceId: string,
  valeurs: { name: string; family: PoleFamily; site_id: string | null },
  position: number,
): Promise<void> {
  const { error } = await client()
    .from('poles')
    .insert({
      workspace_id: workspaceId,
      name: valeurs.name,
      slug: slugifier(valeurs.name),
      family: valeurs.family,
      site_id: valeurs.site_id,
      position,
    });
  if (error) throw error;
}

export type Structure = {
  sites: Site[];
  poles: Pole[];
  members: Member[];
  memberPoles: MemberPole[];
};

/** Annuaire allégé du Tableau : ni téléphones, ni personnes désactivées. */
export async function chargerStructure(workspaceId: string): Promise<Structure> {
  const [sites, poles, members] = await Promise.all([
    client().from('sites').select('*').eq('workspace_id', workspaceId).order('position'),
    client().from('poles').select('*').eq('workspace_id', workspaceId).order('position'),
    client()
      .from('members')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('active', true)
      .order('display_name'),
  ]);

  for (const reponse of [sites, poles, members]) {
    if (reponse.error) throw reponse.error;
  }

  const memberIds = (members.data ?? []).map((membre) => membre.id);
  const memberPoles =
    memberIds.length > 0
      ? await client().from('member_poles').select('*').in('member_id', memberIds)
      : { data: [] as MemberPole[], error: null };
  if (memberPoles.error) throw memberPoles.error;

  return {
    sites: sites.data ?? [],
    poles: poles.data ?? [],
    members: members.data ?? [],
    memberPoles: memberPoles.data ?? [],
  };
}
